import { parseIntegerInput } from '@/lib/integer-input';
import type { ProjectContactDraft } from '@/components/projects/ContactsEditor';
import type { ProjectItem } from '@/components/projects/ItemsEditor';
import type { ProjectDocumentDraft } from '@/components/projects/ProjectDocumentsEditor';

export type ProjectFormCoreValues = {
  name: string;
  description?: string;
  location?: string;
  happeningFor?: string;
  hostClub?: string;
  needAssessment?: string;
  estimatedBeneficiaries?: string;
  expectedCost?: string;
};

export function buildProjectItemsPayload(items: ProjectItem[]) {
  return items.filter((item) => item.name.trim() !== '');
}

export function buildProjectContactsPayload(contacts: ProjectContactDraft[]) {
  return contacts
    .filter((c) => c.phone.trim() || c.email.trim())
    .map((c) => ({
      ...(c.id ? { id: c.id } : {}),
      name: c.name.trim() || undefined,
      designation: c.designation.trim() || undefined,
      phone: c.phone.trim() || undefined,
      email: c.email.trim() || undefined,
      phoneOnWhatsApp: c.phone.trim() ? c.phoneOnWhatsApp : false,
    }));
}

export function buildProjectDocumentsPayload(documents: ProjectDocumentDraft[]) {
  return documents.map((d) => ({
    ...(d.id ? { id: d.id } : {}),
    url: typeof d.url === 'string' ? d.url.trim() : '',
    name: d.name?.trim() || undefined,
    mimeType: d.mimeType || undefined,
    size: d.size,
  }));
}

export function buildProjectCorePayload(
  values: ProjectFormCoreValues,
  options: {
    bannerImageUrl: string;
    rotaryFocusAreaKeys: string[];
    includeEmptyBanner?: boolean;
    alwaysIncludeRotaryKeys?: boolean;
    alwaysIncludeHostClub?: boolean;
  }
) {
  return {
    name: values.name,
    description: values.description || undefined,
    location: values.location || undefined,
    happeningFor: values.happeningFor || undefined,
    hostClub: options.alwaysIncludeHostClub
      ? values.hostClub?.trim() || null
      : values.hostClub?.trim() || undefined,
    needAssessment: values.needAssessment || undefined,
    estimatedBeneficiaries: values.estimatedBeneficiaries
      ? parseIntegerInput(values.estimatedBeneficiaries)
      : undefined,
    expectedCost: values.expectedCost ? parseIntegerInput(values.expectedCost) : undefined,
    ...(options.includeEmptyBanner
      ? {
          bannerImageUrl:
            options.bannerImageUrl.trim().length === 0 ? null : options.bannerImageUrl.trim(),
        }
      : options.bannerImageUrl.trim()
        ? { bannerImageUrl: options.bannerImageUrl.trim() }
        : {}),
    ...(options.alwaysIncludeRotaryKeys || options.rotaryFocusAreaKeys.length > 0
      ? { rotaryFocusAreaKeys: options.rotaryFocusAreaKeys }
      : {}),
  };
}

export function validateIncompleteContacts(contacts: ProjectContactDraft[]) {
  for (let i = 0; i < contacts.length; i++) {
    const row = contacts[i];
    const hasChannel = Boolean(row.phone.trim() || row.email.trim());
    if (!hasChannel && (row.name.trim() || row.designation.trim() || row.phoneOnWhatsApp)) {
      return `Contact row ${i + 1}: add a phone number or email (or remove the incomplete row).`;
    }
  }
  return null;
}
